import React, { useImperativeHandle, useRef } from 'react';
import { TextArea, Typography } from '@navi/web-ui/lib/primitives';
import { ENTER_KEY } from '../../constants';
import styles from './index.module.scss';
import cx from 'classnames';
import SendIcon from '@cp/src/assets/icons/SendIcon';
import { noop } from '@navi/web-ui/lib/utils/common';
import { TextAreaProps } from '@navi/web-ui/lib/primitives/TextArea/types';

interface CommentTextAreaProps extends TextAreaProps {
  value: string;
  onSend?: () => void;
}

const CommentTextArea = React.forwardRef<HTMLTextAreaElement, CommentTextAreaProps>(
  (
    {
      value = '',
      maxLength = 300,
      placeholder = 'Leave a comment...',
      disabled,
      containerClassName,
      onKeyDown,
      onChange,
      onSend,
      ...restProps
    },
    ref
  ) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    useImperativeHandle(ref, () => textareaRef.current as HTMLTextAreaElement);

    const handleInput = () => {
      const textarea = textareaRef.current;

      if (textarea) {
        textarea.style.height = 'auto';
        const maxHeight = 160;

        if (textarea.scrollHeight <= maxHeight) {
          textarea.style.height = textarea.scrollHeight + 'px';
        } else {
          textarea.style.height = maxHeight + 'px';
          textarea.style.overflowY = 'auto';
        }
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === ENTER_KEY && !e.shiftKey) {
        e.preventDefault();
        onKeyDown?.(e);
        const textarea = textareaRef.current;
        if (textarea) {
          textarea.style.height = 'auto';
        }
        if (onSend && typeof onSend === 'function') {
          onSend();
        }
      }
    };

    const handleOnChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (onChange && typeof onChange === 'function' && e.target.value.length <= maxLength) {
        onChange(e);
      }
    };

    const commentLimitReached = value.length >= maxLength;
    const sendDisabled = disabled || commentLimitReached || !value.trim();

    return (
      <div className="relative">
        <TextArea
          ref={textareaRef}
          onChange={handleOnChange}
          onInput={handleInput}
          containerClassName={cx('m-0 w-full !min-w-0', containerClassName)}
          rows={2}
          placeholder={placeholder}
          value={value}
          maxLength={maxLength}
          disabled={disabled}
          onKeyDown={handleKeyDown}
          {...restProps}
        />
        {onSend && typeof onSend === 'function' ? (
          <div
            className={cx(styles.addCommentBtn, {
              [styles.addCommentBtnDisabled]: sendDisabled
            })}
            onClick={sendDisabled ? noop : onSend}
          >
            <SendIcon
              className="rotate-45"
              width={16}
              height={16}
              fillColor1="var(--text-primary)"
            />
          </div>
        ) : null}
        <Typography
          variant="p4"
          color={commentLimitReached ? 'var(--navi-color-orange-base)' : 'var(--grayscale-2)'}
          className={cx({ ['font-semibold']: commentLimitReached }, 'mt-1 text-end')}
        >
          {value.length}/300
        </Typography>
      </div>
    );
  }
);

export default CommentTextArea;
